import { useCallback, useEffect, useState } from "react";

import { api, type Supprime } from "./api";

const DATE = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "short",
  timeStyle: "short",
});

/**
 * Devoirs supprimés dont l'export subsiste.
 *
 * La base est en cascade : sans cette sauvegarde, une suppression emporterait
 * les copies, les notes et les compétences sans retour possible.
 */
export default function Corbeille({
  surRestauration,
  signal = 0,
}: {
  surRestauration: () => void;
  /** Change à chaque suppression : sans quoi la corbeille montrerait encore
   *  son état d'avant, et ce qu'on vient de supprimer n'y serait pas. */
  signal?: number;
}) {
  const [entrees, setEntrees] = useState<Supprime[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState(false);

  const recharger = useCallback(() => {
    api.lireCorbeille().then(setEntrees).catch(() => setEntrees([]));
  }, [signal]);

  useEffect(recharger, [recharger]);

  if (entrees.length === 0) return null;

  const restaurer = async (entree: Supprime) => {
    try {
      // Une classe restaure aussi ses élèves et tous ses devoirs.
      if (entree.type === "classe") {
        const classe = await api.restaurerClasse(entree.fichier);
        setMessage(`Classe ${classe.code} restaurée avec ses devoirs.`);
      } else if (entree.type === "qcm") {
        // Ses questions sont retrouvées dans la banque : elles n'ont jamais
        // été détruites, l'export n'en portait que les identifiants.
        await api.restaurerQcm(entree.fichier);
        setMessage(`« ${entree.titre} » restauré.`);
      } else {
        const restaure = await api.restaurerDs(entree.fichier);
        setMessage(`« ${restaure.titre} » restauré.`);
      }
      recharger();
      surRestauration();
    } catch (e) {
      setMessage((e as Error).message);
    }
  };

  return (
    <div className="corbeille">
      <button type="button" className="titre-corbeille" onClick={() => setOuverte(!ouverte)}>
        {ouverte ? "▾" : "▸"} Corbeille — {entrees.length} élément
        {entrees.length > 1 ? "s" : ""} supprimé{entrees.length > 1 ? "s" : ""}
      </button>

      {ouverte && (
        <>
          <p className="aide">
            Chaque suppression laisse une sauvegarde complète — questions,
            barème ajusté, compétences, notes, appréciations, et pour une classe
            ses élèves, ses devoirs et ses QCM. Celle d'un QCM emporte en outre
            ses permutations, sans lesquelles les réponses ne voudraient plus
            rien dire ; ses questions, elles, n'ont jamais quitté la banque.
            Aucune sauvegarde n'est effacée automatiquement : les fichiers sont
            dans <code>data/corbeille/</code>.
          </p>
          {message && <p className="message">{message}</p>}
          <table className="liste-ds">
            <tbody>
              {entrees.map((entree) => (
                <tr key={entree.fichier}>
                  <td>
                    {entree.titre}
                    <div className="fichier">
                      {entree.classe} · supprimé le{" "}
                      {DATE.format(new Date(entree.supprime_le))}
                    </div>
                  </td>
                  <td className="nombre">
                    {entree.type === "classe"
                      ? `${entree.eleves} élèves · ${entree.devoirs} devoirs`
                      : `${entree.questions} questions`}
                  </td>
                  <td className="nombre">{entree.notes} notes</td>
                  <td>
                    <button type="button" onClick={() => restaurer(entree)}>
                      Restaurer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
